import {
  BookOpen,
  BookOpenText,
  GitBranch,
  GraduationCap,
  Layers,
  Megaphone,
  MessageCircle,
  Newspaper,
  Presentation,
  ScrollText,
  Clapperboard,
  Music2,
  Smartphone,
  Users,
} from 'lucide-react';
import { dashboardShowcaseMovies } from '@/data/dashboardShowcaseMovies';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/** Creation flows — shared by /stories/flow, dashboard, and landing. */
export const STORY_FLOWS = [
  {
    id: 'unified_story',
    available: true,
    title: 'Story',
    shortTitle: 'Story',
    subtitle: 'Confirm the story, then each scene picks its own modules',
    explainer: 'One create path. Twinnee reads the prompt and files, you lock time, format, and who appears, then each scene can be a still, avatar, scrape, stock clip, or a live take.',
    description: 'Dynamic story flow: context, confirmation, a screenplay, then per-scene generation.',
    icon: Clapperboard,
    story_type: 'unified_story',
    video_style: 'cinematic',
    appRoute: '/dashboard?agent=new&flow=unified_story',
    tone: 'storytelling',
    accent: '#2DD4BF',
    prompts: ['Prompt and files', 'Lock voices and faces', 'Approve the screenplay'],
    previewVideoUrl: dashboardShowcaseMovies[4]?.videoUrl,
    previewAlt: 'Story flow — confirm context, then generate per scene',
  },
  {
    id: 'avatar_cast',
    available: true,
    title: 'AvatarCast Studio',
    shortTitle: 'AvatarCast',
    subtitle: 'HeyGen AI Avatar & Lip-Sync Video',
    explainer: 'Generate customized AI avatars with HeyGen, record voice narration live, and render lip-synced talking videos.',
    description: 'Create high-fidelity AI Avatars, review your look, record voice narration, and synthesize HD talking videos with HeyGen.',
    icon: Users,
    story_type: 'avatar_cast',
    video_style: 'talking_head',
    appRoute: '/avatar-cast',
    tone: 'professional',
    accent: '#A855F7',
    prompts: ['HeyGen Avatar', 'Live Voice Record', 'Lip-Synced Video'],
    previewVideoUrl: dashboardShowcaseMovies[0]?.videoUrl,
    previewAlt: 'AvatarCast Studio — HeyGen AI Avatar & Talking Video',
  },
  {
    id: 'narration',
    available: true,
    title: 'Narration',
    shortTitle: 'Narration',
    subtitle: 'Warm narrator guides scene to scene',
    explainer: 'One voice carries the story—ideal for bedtime tales, audiobooks, and moral journeys.',
    description: 'Bedtime stories, moral tales, read-aloud videos, and gentle character journeys.',
    icon: BookOpenText,
    story_type: 'narration_based',
    video_style: 'narrated',
    appRoute: '/narration/create',
    tone: 'bedtime',
    accent: '#6366F1',
    prompts: ['Narrator-led', 'Scene summaries', 'Spoken pacing'],
    previewVideoUrl: dashboardShowcaseMovies[4]?.videoUrl,
    previewAlt: 'Narration flow — character-led story scene',
  },
  {
    id: 'script_based',
    available: true,
    title: 'Script-based',
    shortTitle: 'Script',
    subtitle: 'Contextual scenes → TTS → WhisperX timing',
    explainer: 'Your script split into image-sized scene beats (verbatim), then narrated and aligned.',
    description: 'Finished scripts split verbatim, narrated per statement with precise alignment.',
    icon: ScrollText,
    story_type: 'script_based',
    video_style: 'narrated',
    appRoute: '/script-based/create',
    tone: 'storytelling',
    accent: '#7C3AED',
    prompts: ['Paste script', 'No rewrite', 'WhisperX timing'],
    previewVideoUrl: dashboardShowcaseMovies[4]?.videoUrl,
    previewAlt: 'Script-based flow — verbatim narration',
  },
  {
    id: 'storybuilder',
    available: true,
    title: 'Storybuilder',
    shortTitle: 'Storybuilder',
    subtitle: 'PDF pages → image + text → narration video',
    explainer: 'Upload a PDF. Each page’s image and text become a narrated slideshow segment in the editor.',
    description: 'Turn storybooks and decks into page-by-page narration with audio and video in Scene/Editor views.',
    icon: BookOpen,
    story_type: 'storybuilder',
    video_style: 'narrated',
    appRoute: '/storybuilder/create',
    tone: 'storytelling',
    accent: '#2DD4BF',
    prompts: ['Upload PDF', 'Page image + text', 'Editor-ready'],
    previewVideoUrl: dashboardShowcaseMovies[4]?.videoUrl,
    previewAlt: 'Storybuilder — PDF to narration video',
  },
  {
    id: 'reels_shorts',
    available: true,
    title: 'REEL&SHORTS',
    shortTitle: 'Reels',
    subtitle: 'Snippets → transcribe → AI cut + B-ROLL',
    explainer: 'Upload talking clips. Each is stored on S3, split into video and audio, transcribed verbatim with Gemini 3.5 Transcribe Live (editable), then ChatGPT orders them without changing text and suggests split-screen, overlay, and serial-card B-ROLL.',
    description: 'Short-form reel pipeline from multiple uploaded snippets with an AI editor and catalog B-ROLL layouts.',
    icon: Smartphone,
    story_type: 'reels_shorts',
    video_style: 'short_form',
    appRoute: '/reels-shorts/create',
    tone: 'energetic',
    accent: '#F43F5E',
    prompts: ['Multi-clip upload', 'Gemini transcribe', 'AI cut + B-ROLL'],
    previewVideoUrl: dashboardShowcaseMovies[3]?.videoUrl,
    previewAlt: 'REEL&SHORTS — snippet reels with B-ROLL overlays',
  },
  {
    id: 'song_based',
    available: true,
    title: 'SongAI Flow',
    shortTitle: 'SongAI',
    subtitle: 'Upload → transcribe → AI segments → beat-sync music video',
    explainer: 'Upload a song, define cinematic metadata, transcribe lyrics, AI-segment verses/chorus, WhisperX timing, theme + screenplay, and beat-sync visuals.',
    description: 'Standalone music-video pipeline: lyric intelligence, screenplay from theme, waveform-synced animation, full creative metadata.',
    icon: Music2,
    story_type: 'song_based',
    video_style: 'narrated',
    appRoute: '/song-based/create',
    tone: 'storytelling',
    accent: '#EC4899',
    prompts: ['Upload song', 'AI stanza split', 'Beat-sync motion'],
    previewVideoUrl: dashboardShowcaseMovies[4]?.videoUrl,
    previewAlt: 'SongAI Flow — music video from lyrics',
  },
  {
    id: 'tutorial',
    available: true,
    title: 'Tutorial Flow',
    shortTitle: 'Tutorial',
    subtitle: 'Multi-pass concept → artifact → video pipeline',
    explainer: 'Staged refinement: decompose a topic, expand with 3+ instructional artifacts per concept, then render the full timeline.',
    description: 'Beginner-first tutorials with DFS-ordered scenes, diagrams, analogies, and synced narration.',
    icon: Layers,
    story_type: 'tutorial_flow',
    video_style: 'educational',
    appRoute: '/script-audio-align',
    navState: { prefill: { mode: 'topic' } },
    tone: 'educational',
    accent: '#8B5CF6',
    prompts: ['Concept tree', '3+ artifacts', 'DFS timeline'],
    previewVideoUrl: dashboardShowcaseMovies[2]?.videoUrl,
    previewAlt: 'Tutorial flow — staged educational pipeline',
  },
  {
    id: 'tutorial_v3',
    available: true,
    title: 'Tutorial Flow 3',
    shortTitle: 'Tree v3',
    subtitle: 'Universal concept tree from name + topic',
    explainer: 'Expert knowledge architect prompt: exhaustive concept tree, learning path, dependencies, Feynman explanation — returned as structured JSON.',
    description: 'Quick curriculum design — hierarchical concepts with prerequisites, roadmap, and relationship tables.',
    icon: GitBranch,
    story_type: 'tutorial_flow_v3',
    video_style: 'educational',
    appRoute: '/script-audio-align',
    navState: { prefill: { mode: 'topic' } },
    tone: 'educational',
    accent: '#A78BFA',
    prompts: ['Name + topic', 'Full concept tree', 'JSON API'],
    previewVideoUrl: dashboardShowcaseMovies[2]?.videoUrl,
    previewAlt: 'Tutorial Flow 3 — universal concept tree',
  },
  {
    id: 'tutorial_v4',
    available: true,
    title: 'Tutorial Flow 4',
    shortTitle: 'Outline story',
    subtitle: 'Universal concept tree outline (A–J)',
    explainer: 'Single-step outline story: exhaustive concept tree, learning path, dependencies, Feynman explanation — structured JSON for curriculum design.',
    description: 'Create a hierarchical outline story from name + topic using the universal concept tree prompt.',
    icon: BookOpen,
    story_type: 'tutorial_flow_v4',
    video_style: 'educational',
    appRoute: '/script-audio-align',
    navState: { prefill: { mode: 'topic' } },
    tone: 'educational',
    accent: '#14B8A6',
    prompts: ['Name + topic', 'Sections A–J', 'Outline JSON'],
    previewVideoUrl: dashboardShowcaseMovies[2]?.videoUrl,
    previewAlt: 'Tutorial Flow 4 — outline story',
  },
  {
    id: 'learning',
    available: true,
    title: 'Learning & Training',
    shortTitle: 'Learning',
    subtitle: 'Teach with explain, practice, recap scenes',
    explainer: 'Structured lessons with slides, narration sync, and step-by-step topic depth.',
    description: 'Classroom lessons, tutorials, training modules, and skill-building stories.',
    icon: GraduationCap,
    story_type: 'learning_training',
    video_style: 'educational',
    appRoute: '/script-audio-align',
    navState: { prefill: { mode: 'topic' } },
    tone: 'educational',
    accent: '#10B981',
    prompts: ['Learning objective', 'Step-by-step', 'Review'],
    previewVideoUrl: dashboardShowcaseMovies[2]?.videoUrl,
    previewAlt: 'Learning flow — educational visual scene',
  },
  {
    id: 'pitch_recording',
    available: true,
    title: 'Pitch recording',
    shortTitle: 'Pitch',
    subtitle: 'Script → teleprompter → record → timeline',
    explainer: 'Paste a timed pitch script, record each part with or without camera, map takes to the editor.',
    description: 'Founder pitches, hackathon demos, investor decks — record VO and optional camera per section.',
    icon: Presentation,
    story_type: 'pitch_recording',
    video_style: 'pitch',
    appRoute: '/pitch/create',
    tone: 'professional',
    accent: '#F43F5E',
    prompts: ['Timed script', 'Teleprompter', 'Camera + audio'],
    previewVideoUrl: dashboardShowcaseMovies[0]?.videoUrl,
    previewAlt: 'Pitch recording — founder on camera',
  },
  {
    id: 'news_video',
    available: true,
    title: 'News Video',
    shortTitle: 'News',
    subtitle: 'Anchor script, voice, bulletin overlays',
    explainer: 'Bulletin-style edits with prompter script, tickers, logos, and reference footage.',
    description: 'News prompter, references, logos, tickers, and ad overlays.',
    icon: Newspaper,
    story_type: 'news_video',
    video_style: 'news',
    appRoute: '/news/create',
    tone: 'professional',
    accent: '#0EA5E9',
    prompts: ['Topic + refs', 'Prompter voice', 'Ticker'],
    previewVideoUrl: dashboardShowcaseMovies[0]?.videoUrl,
    previewAlt: 'News flow — cinematic bulletin scene',
  },
  {
    id: 'mascot_animation',
    available: true,
    title: 'News Mascot Animation',
    shortTitle: 'Mascots',
    subtitle: 'Photo → 3D cartoon sheet → transparent logo clips',
    explainer: 'Upload 1–2 faces, generate green-screen 3D mascot turnarounds and expressions, auto cut-out, then export slow transparent bumpers for your channel.',
    description: 'Animated news-channel mascots from reference photos — handshake, victory, fist pump, and more.',
    icon: TwinneeLogo,
    story_type: 'mascot_animation',
    video_style: 'news',
    appRoute: '/mascot/create',
    tone: 'professional',
    accent: '#22D3EE',
    prompts: ['Green-screen sheet', 'Auto cut-out', 'Transparent WebM'],
    previewVideoUrl: dashboardShowcaseMovies[0]?.videoUrl,
    previewAlt: 'News mascot animation — transparent channel bumper',
  },
  {
    id: 'advertisement',
    available: true,
    title: 'Advertisement',
    shortTitle: 'Ads',
    subtitle: 'Short promotional spots from an idea',
    explainer: 'Hook, benefit, and CTA in a tight spot—built for social, promos, and campaigns.',
    description: 'Product explainers, campaign spots, social ads, and awareness videos.',
    icon: Megaphone,
    story_type: 'advertisement',
    video_style: 'advertisement',
    appRoute: '/dashboard?agent=new&flow=advertisement',
    tone: 'funny',
    accent: '#F97316',
    prompts: ['Hook & CTA', 'Punchy copy', 'Social-ready'],
    previewVideoUrl: dashboardShowcaseMovies[3]?.videoUrl,
    previewAlt: 'Advertisement flow — portrait promo scene',
  },
  {
    id: 'dialogue',
    available: true,
    title: 'Dialogue Story',
    shortTitle: 'Dialogue',
    subtitle: 'Multiple characters via back-and-forth',
    explainer: 'Each character speaks in turn—conversations, roleplay, and ensemble scenes.',
    description: 'Group adventures, roleplay, conversations, and character-driven lessons.',
    icon: MessageCircle,
    story_type: 'dialogue_story',
    video_style: 'dialogue',
    appRoute: '/dialogue/create',
    tone: 'adventure',
    accent: '#38BDF8',
    prompts: ['Multi-speaker', 'Character voices', 'Conversations'],
    previewVideoUrl: dashboardShowcaseMovies[5]?.videoUrl,
    previewAlt: 'Dialogue flow — multi-character scene',
  },
];

/** Strip React components (e.g. icon) — History state must be structured-cloneable. */
export function storyFlowNavigationState(flow) {
  if (!flow) return undefined;
  return {
    id: flow.id,
    title: flow.title,
    story_type: flow.story_type,
    video_style: flow.video_style,
    tone: flow.tone,
    moral_theme: flow.moral_theme ?? '',
  };
}

export function navigateToStoryFlow(flow, navigate, { authenticated = false, unifiedOnly = false } = {}) {
  // All new creation goes through Create with agent (no standalone wizard starts).
  if (!authenticated) {
    navigate('/register');
    return;
  }
  const flowId = unifiedOnly ? 'unified_story' : (flow?.id || '');
  const params = new URLSearchParams();
  params.set('agent', 'new');
  if (flowId) params.set('flow', flowId);
  navigate(`/dashboard?${params.toString()}`);
}
